// @ts-expect-error autotypes instance file
import type __AT__ from "@/autotypes";

import * as React from "react";

import { useFetch } from "./useFetch";
import { usePage } from "./usePage";
import { useSite } from "./useSite";
import { concatParams } from "../utils";

interface Item {
	description: string;
	image: string;
	similarity: number;
	structuredData: Record<PropertyKey, unknown>;
	template: string;
	title: string;
	url: string;
}

interface Response {
	page?: number;
	items?: Array<Item>;
	totalItems?: number;
}

interface Options {
	pageId?: number;
	fields?: Array<string>;
	quantity?: number;
	useStructuredData?: boolean;
	template?: __AT__.Templates;
}

function createSetQuery(
	publicApiUrl: string,
	siteId: number,
	pageId: number | undefined,
	languageId: number,
	templateType: __AT__.Templates,
	setQueryUrl?: React.Dispatch<React.SetStateAction<string | undefined>>,
) {
	if (!publicApiUrl || !setQueryUrl) {
		return () => "";
	}

	return (options?: Options) => {
		const { fields, quantity, template, useStructuredData } = options || {};

		const urlWithParams = concatParams({
			baseUrl: `${publicApiUrl}/ai/pages/related/`,
			params: {
				p: pageId,
				l: languageId,
				s: siteId,
				q: quantity,
				d: useStructuredData ? "on" : undefined,
				f: fields?.join(",") || "",
				t: template || templateType,
			},
		});

		setQueryUrl(urlWithParams);
	};
}

export function usePageRelatedContent() {
	const { publicApiUrl, siteId } = useSite();
	const { id: pageId, languageId, template } = usePage();
	const [queryUrl, setQueryUrl] = React.useState<string | undefined>(undefined);

	if (!publicApiUrl || !siteId || !languageId) {
		return;
	}

	const setQuery = React.useMemo(
		() =>
			createSetQuery(
				publicApiUrl,
				siteId,
				pageId,
				languageId,
				template?.templateType,
				setQueryUrl,
			),
		[publicApiUrl, pageId, siteId, template],
	);

	const { isError, isLoading, msg, data, isFirstFetch } =
		useFetch<Response>(queryUrl);

	return [
		{ query: data, isLoading, isError, msg, isFirstFetch },
		setQuery,
	] as const;
}
